import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import * as analytics from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  BulkAssemblyMethod,
  SKIP_BIN_DISPLAY_TEXT,
  SKIP_BIN_SENTINEL,
  SpecifiedFragmentInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {getNameResolver, NameResolutionType} from 'molbio/bulk-assembly/util/NameResolution';
import OptionCellDropdown from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {GRAY} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

const NOOP_SENTINEL = 'NOOP_SENTINEL';

function FragmentOption({
  binColor,
  className,
  polymerName,
}: {
  binColor: string;
  className?: string;
  polymerName: string;
}): JSX.Element {
  return (
    <span className={className}>
      <span className={css(styles.constructFragmentColor)} style={{backgroundColor: binColor}} />
      <span>{polymerName}</span>
    </span>
  );
}

function buildOptionsFromFragmentDatas(
  fragmentDatas: Array<SpecifiedFragmentInfo>,
  binColor: string
): Array<string | {node: ReactNode; onUpdateKey: string}> {
  const options: Array<string | {node: ReactNode; onUpdateKey: string}> = [
    {
      node: <span>Skip</span>,
      onUpdateKey: SKIP_BIN_SENTINEL,
    },
  ];

  for (const fragmentData of fragmentDatas) {
    if (fragmentData.errors.length === 0) {
      options.push({
        node: <FragmentOption binColor={binColor} polymerName={fragmentData.polymerName} />,
        onUpdateKey: fragmentData.fragmentCid,
      });
    } else {
      options.push({
        node: (
          <Popover
            renderContent={() => (
              <>
                {fragmentData.errors.map((error) => (
                  <div>{error}</div>
                ))}
              </>
            )}
            placement={FloatingPlacement.RIGHT}
          >
            <FragmentOption
              binColor={binColor}
              className={css(styles.disabledOption)}
              polymerName={fragmentData.polymerName}
            />
          </Popover>
        ),
        onUpdateKey: NOOP_SENTINEL,
      });
    }
  }

  return options;
}

function getFragmentDataById(
  fragmentDatas: Array<SpecifiedFragmentInfo>,
  fragmentCid: string
): SpecifiedFragmentInfo | null {
  return fragmentDatas.find((data) => data.fragmentCid === fragmentCid) ?? null;
}

export interface BulkAssemblyConstructFragmentSelectorProps {
  /** The current value of the cell, if any */
  typedValue: TypedValue | null;
  /** A function to be called when a new value is selected in the dropdown. */
  onUpdate: (typedValue: TypedValue) => void;
  /** Data about the fragments to be displayed in the dropdown. */
  fragmentDatas: Array<SpecifiedFragmentInfo>;
  /** The color of the bin represented by this column / cell. */
  binColor: string;
  /** Whether or not this cell should be locked. */
  locked: boolean;
  // The following two props are necessary for analytics, but not the function of the cell itself.
  /** The assembly method being used. */
  method: BulkAssemblyMethod;
  /** The API id of the assembly. */
  assemblyId: string;
}

export default function BulkAssemblyConstructFragmentSelector(
  props: BulkAssemblyConstructFragmentSelectorProps
): JSX.Element {
  const constructFragmentResolver = getNameResolver(
    props.fragmentDatas,
    (fragmentData) => fragmentData.polymerName
  );
  const cellText = props.typedValue ? formatTypedValue(props.typedValue) : '';
  const resolvedConstructFragment = constructFragmentResolver(cellText);
  let staticDisplay: JSX.Element;
  if (resolvedConstructFragment.type === NameResolutionType.FOUND) {
    staticDisplay = <FragmentOption binColor={props.binColor} polymerName={cellText} />;
  } else if (
    props.typedValue?.type === BaseTypedValueType.STRING &&
    props.typedValue?.rawValue === SKIP_BIN_SENTINEL
  ) {
    staticDisplay = <span>{SKIP_BIN_DISPLAY_TEXT}</span>;
  } else {
    staticDisplay = <span>{cellText}</span>;
  }

  if (props.locked) {
    // OptionCellDropdown's "locked" gives a dropdown of grayed-out choices. In our case, we want no dropdown
    // at all, but still want to show the selected fragment, so render as a separate case.
    return staticDisplay;
  } else {
    return (
      <OptionCellDropdown
        locked={false}
        isSelected={true}
        options={buildOptionsFromFragmentDatas(props.fragmentDatas, props.binColor)}
        onUpdate={(onUpdateKey) => {
          if (onUpdateKey !== NOOP_SENTINEL) {
            if (onUpdateKey === SKIP_BIN_SENTINEL) {
              props.onUpdate({
                type: BaseTypedValueType.STRING,
                rawValue: SKIP_BIN_SENTINEL,
              });
            } else {
              props.onUpdate({
                type: BaseTypedValueType.CLIENT_IDENTIFIABLE,
                id: onUpdateKey,
                name: getFragmentDataById(props.fragmentDatas, onUpdateKey)?.polymerName ?? '',
              });
            }
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.EDITED_BULK_ASSEMBLY_CONSTRUCT_IN_TABLE, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
              cloningMethod: props.method,
              assemblyId: props.assemblyId,
            });
          }
        }}
        fullWidth={true}
        lightHoverBackground={true}
        staticDisplay={staticDisplay}
      />
    );
  }
}

const styles = StyleSheet.create({
  disabledOption: {
    color: GRAY,
    cursor: 'not-allowed',
  },
  constructFragmentColor: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 4,
    marginRight: 6,
  },
});
